/**
 * Registry chat modelov (shared metadata) — seja GaMS migracija.
 *
 * Ena sama tabela, ki jo importata KLIENT (labeli, badge, nastavitve) in SERVER
 * (mapiranje id → dejanski LanguageModel v `lib/ai/provider.ts`). Tu NI skrivnosti
 * (API ključi / baseURL so v env na strežniku) — samo opisna metapodatka.
 *
 * Načelo (docs/LLM_MIGRACIJA_GAMS.md §5.1): ne forkaj kode, parametriziraj ponudnika.
 * Migrira SAMO končni chat streaming odgovor; intent/extraktorji/verifier (`model`)
 * in nomotehnika/mnenje (`modelPro`) ostanejo Gemini.
 */

export type ChatModelId =
  | 'gemini-flash'
  | 'gams2-27b'
  | 'gams3-12b'
  | 'gams-9b-nemotron'
  | 'custom';

export interface ChatModelMeta {
  id: ChatModelId;
  label: string;
  shortLabel: string;
  description: string;
  provider: 'gemini' | 'gams' | 'custom';
  /** Točen id modela za OpenAI-compatible /v1 endpoint (samo GaMS). */
  gamsModelName?: string;
  /** Operativno kontekstno okno (tokeni) — napaja per-model trim proračun. */
  contextTokens: number;
  /** Ali endpoint podpira `system` vlogo. Gemini + GaMS3 da; GaMS2/9B/custom ne. */
  supportsSystemRole: boolean;
  /** Sproži »Made in Slovenia« badge. */
  slovenian: boolean;
  /** Reasoning-tuned model (informativno za UI). */
  reasoning?: boolean;
}

export const CHAT_MODELS: Record<ChatModelId, ChatModelMeta> = {
  'gemini-flash': {
    id: 'gemini-flash',
    label: 'Gemini 2.5 Flash',
    shortLabel: 'Gemini',
    description: 'Google Vertex AI — privzeti, stabilni model (okno 1M, operativno ~125k).',
    provider: 'gemini',
    contextTokens: 125_000,
    supportsSystemRole: true,
    slovenian: false,
  },
  'gams2-27b': {
    id: 'gams2-27b',
    label: 'GaMS2-27B-Instruct',
    shortLabel: 'GaMS 27B',
    description: 'Slovenski model (CJVT/IJS). Najkakovostnejši; okno 32k. Priporočen privzetek za GaMS.',
    provider: 'gams',
    gamsModelName: 'GaMS2-27B-Instruct',
    contextTokens: 32_000,
    supportsSystemRole: false,
    slovenian: true,
  },
  'gams3-12b': {
    id: 'gams3-12b',
    label: 'GaMS3-12B-Instruct',
    shortLabel: 'GaMS 12B',
    description: 'Slovenski model. Veliko okno 131k → manj rezanja konteksta. Podpira system-role.',
    provider: 'gams',
    gamsModelName: 'GaMS3-12B-Instruct',
    contextTokens: 131_000,
    supportsSystemRole: true,
    slovenian: true,
  },
  'gams-9b-nemotron': {
    id: 'gams-9b-nemotron',
    label: 'GaMS-9B-Instruct-Nemotron',
    shortLabel: 'GaMS 9B',
    description: 'Slovenski reasoning model. Majhno okno 8k → za kratke, sklepanje-težke poizvedbe.',
    provider: 'gams',
    gamsModelName: 'GaMS-9B-Instruct-Nemotron',
    contextTokens: 8_000,
    supportsSystemRole: false,
    slovenian: true,
    reasoning: true,
  },
  custom: {
    id: 'custom',
    label: 'Custom endpoint',
    shortLabel: 'Custom',
    description: 'Lasten OpenAI-compatible endpoint (CLORE.AI ipd.). Samo admin.',
    provider: 'custom',
    contextTokens: 32_000,
    supportsSystemRole: false,
    slovenian: true,
  },
};

export const DEFAULT_CHAT_MODEL: ChatModelId = 'gemini-flash';

/** Varno razreši id iz (nezaupanja vrednega) vhoda; fallback na privzetek. */
export function resolveChatModelId(id: unknown): ChatModelId {
  return typeof id === 'string' && id in CHAT_MODELS
    ? (id as ChatModelId)
    : DEFAULT_CHAT_MODEL;
}
